import { useCallback, useState, useEffect, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import {
  Button,
  Dialog,
  DialogConfirmation,
  DialogContent,
  DialogHeader,
  FilterDropdownItemProps
} from '@carto/meridian-ds/components'

import { Connection } from '../../types'
import PrivacySettingsRadioGroup from 'features/common/ui/sharing/PrivacySettingsRadioGroup'
import { Group } from 'features/groups'
import { CommonPrivacy, CommonSharingScope } from 'features/common'
import { PrivatePrivacy, SharedPrivacy } from 'features/common/ui/sharing/SharingOptions'
import ConnectionSharedWithOrganizationMode from './ConnectionSharedWithOrganizationMode'
import SharedWithOrganizationMode from 'features/common/ui/sharing/SharedWithOrganizationMode/SharedWithOrganizationMode'
import ConnectionShareDialogFooter from './ConnectionShareDialogFooter'
import UnsharingConnectionWarning from './UnsharingConnectionWarning'
import DisableViewerCredentialsConfirmation from './DisableViewerCredentialsConfirmation'
import { SharedOptionDescription } from 'features/common/ui/sharing/ShareOptionShared/ShareOptionShared'
import { FeatureFlag, useFeatureFlag, useStringFeatureFlag } from 'app/ui/useFeatureFlag'
import { SharingProvider, useSharingContext } from 'features/common/ui/sharing/SharingContext'
import { useConnectionTypes, useRequiresUserCredentials } from './connectionTypes'

export type ConnectionSharingOptions = Pick<
  Connection,
  'config' | 'privacy' | 'groups' | 'viewerCredentials' | 'sharingScope'
>

function ConnectionShareDialogUiWrapper(props) {
  return (
    <SharingProvider>
      <ConnectionShareDialogUi {...props} />
    </SharingProvider>
  )
}

function ConnectionShareDialogUi({
  open,
  onClose,
  connection,
  onChange,
  allowSharing,
  isUpdating = false
}: {
  open: boolean
  onClose: () => void
  connection: Pick<Connection, 'id' | 'provider_id'> & ConnectionSharingOptions
  onChange: (sharingUpdate: Partial<ConnectionSharingOptions>) => void
  allowSharing: boolean
  isUpdating?: boolean
}) {
  const { dirty } = useSharingContext()
  const [showNotSavedChangesAlert, setShowNotSavedChangesAlert] = useState(false)
  const enableSnowflakeRowLevelSecurity = useFeatureFlag(FeatureFlag.SnowflakeRowLevelSecurity)
  const databricksRestConnectionEnabled = useStringFeatureFlag(FeatureFlag.DatabricksDeprecateVCPAT)
  const {
    isBQWithSSO,
    isBQWithWIF,
    isDatabricksWithU2m,
    isDatabricksWithM2m,
    isDatabricksRest,
    isSnowflakeWithSSO,
    isSnowflakeWithKeyPair,
    providerSupportViewerCredentials,
    isViewerCredentialsDisabledBigquery,
    isViewerCredentialsDisabledSnowflake,
    isViewerCredentialsDisabledDatabricks,
    isViewerCredentialsDisabled,
    disabledEntireOrganization,
    disabledGroups,
    disableVCPAT
  } = useConnectionTypes(enableSnowflakeRowLevelSecurity, databricksRestConnectionEnabled, connection)
  const tooltipEntireOrganization =
    (isBQWithWIF && <FormattedMessage id="connections.shareDialog.viewerCredentials.disabledBigqueryWif" />) ||
    (isBQWithSSO && <FormattedMessage id="connections.shareDialog.viewerCredentials.disabledBigquery" />) ||
    (isSnowflakeWithSSO && <FormattedMessage id="connections.shareDialog.viewerCredentials.disabledSnowflake" />) ||
    (isDatabricksWithU2m && <FormattedMessage id="connections.shareDialog.viewerCredentials.disabledDatabricksU2M" />)

  const tooltipGroups =
    (isBQWithWIF && <FormattedMessage id="connections.shareDialog.viewerCredentials.disabledBigqueryWif" />) ||
    (isBQWithSSO && <FormattedMessage id="connections.shareDialog.viewerCredentials.disabledBigquery" />) ||
    (isSnowflakeWithSSO && <FormattedMessage id="connections.shareDialog.viewerCredentials.disabledSnowflake" />) ||
    (isDatabricksWithU2m && <FormattedMessage id="connections.shareDialog.viewerCredentials.disabledDatabricksU2M" />)

  const tooltipViewerCredentials =
    (isViewerCredentialsDisabledBigquery && <FormattedMessage id="common.shareDialog.tooltip.serviceAccount" />) ||
    (isViewerCredentialsDisabledSnowflake &&
      (isSnowflakeWithSSO ? (
        <FormattedMessage id="connections.shareDialog.viewerCredentials.disabledSnowflake" />
      ) : isSnowflakeWithKeyPair ? (
        <FormattedMessage id="connections.shareDialog.viewerCredentials.disabledKeyPairSnowflake" />
      ) : (
        <FormattedMessage id="connections.shareDialog.viewerCredentials.disabledServiceAccountSnowflake" />
      ))) ||
    (isViewerCredentialsDisabledDatabricks && isDatabricksWithM2m && (
      <FormattedMessage id="connections.shareDialog.viewerCredentials.disabledDatabricksM2M" />
    )) ||
    (isViewerCredentialsDisabledDatabricks && isDatabricksRest && (
      <FormattedMessage id="connections.shareDialog.viewerCredentials.disabledDatabricksRest" />
    ))

  const [showUnshareConnectionWarning, setShowUnshareConnectionWarning] = useState(false)
  const [showDisableViewerCredentialWarning, setShowDisableViewerCredentialWarning] = useState(false)
  const [privacy, setPrivacy] = useState<CommonPrivacy>(connection.privacy || CommonPrivacy.Private)
  const [mode, setMode] = useState<string>(isBQWithSSO || isSnowflakeWithSSO || isBQWithWIF ? 'viewer' : 'organization')
  const [groups, setGroups] = useState<Group[]>(connection.groups || [])

  const allowSharingConnection = useRequiresUserCredentials(allowSharing, connection)
  const disableEntireOrganization = useMemo(() => {
    return !allowSharing && allowSharingConnection
  }, [allowSharingConnection, allowSharing])

  const SharedPrivacyOption: FilterDropdownItemProps = useMemo(() => {
    if (allowSharingConnection) {
      return SharedPrivacy
    }

    return {
      ...SharedPrivacy,
      disabled: true,
      tooltipId: 'connections.shareDialog.disabledEntireOrganization',
      tooltipPlacement: 'top',
      tooltipEnterNextDelay: 50
    }
  }, [allowSharingConnection])

  useEffect(() => {
    if (connection) {
      setPrivacy(connection.privacy ?? CommonPrivacy.Shared)
      setGroups(connection.groups ?? [])
      if (connection.groups?.length || connection.sharingScope === CommonSharingScope.Specific) {
        setMode('groups')
      } else if (
        connection.viewerCredentials ||
        isBQWithSSO ||
        isSnowflakeWithSSO ||
        isBQWithWIF ||
        isDatabricksWithU2m ||
        disableEntireOrganization
      ) {
        setMode('viewer')
      } else {
        setMode('organization')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connection.id, isBQWithSSO, isSnowflakeWithSSO, allowSharingConnection])

  const update = useCallback(
    (update: Partial<ConnectionSharingOptions>) => {
      if (update.privacy !== undefined) {
        setPrivacy(update.privacy)
      }
      if (update.groups !== undefined) {
        setGroups(update.groups)
      }
      onChange(update)
    },
    [onChange]
  )

  const updatePrivacy = useCallback(
    (privacy) => {
      let viewerCredentials = privacy === CommonPrivacy.Private ? false : undefined
      if (
        privacy === CommonPrivacy.Shared &&
        (isBQWithSSO || isBQWithWIF || isSnowflakeWithSSO || isDatabricksWithU2m || (isDatabricksRest && !disableVCPAT))
      ) {
        viewerCredentials = true
      }
      const sharingScope = privacy === CommonPrivacy.Private ? undefined : CommonSharingScope.Organization
      update({ privacy, viewerCredentials, sharingScope })
    },
    [update, isBQWithSSO, isBQWithWIF, isSnowflakeWithSSO, isDatabricksWithU2m, isDatabricksRest, disableVCPAT]
  )

  const handleChangeShareOption = useCallback(
    (item) => {
      if (item.id === CommonPrivacy.Private && connection.privacy !== CommonPrivacy.Private) {
        // Show warning: change from Shared to private
        setShowUnshareConnectionWarning(true)
      } else {
        updatePrivacy(item.id)
      }
    },
    [updatePrivacy, connection]
  )

  const handleOnSharingOrganizationMode = useCallback(
    (mode) => {
      if (connection.viewerCredentials && mode !== 'viewer') {
        setShowDisableViewerCredentialWarning(true)
      } else if (mode === 'organization') {
        update({
          privacy: CommonPrivacy.Shared,
          groups: [],
          viewerCredentials: false,
          sharingScope: CommonSharingScope.Organization
        })
      } else if (mode === 'viewer') {
        update({
          privacy: CommonPrivacy.Shared,
          groups: [],
          viewerCredentials: true,
          sharingScope: CommonSharingScope.Organization
        })
      } else if (mode === 'groups') {
        update({
          privacy: CommonPrivacy.Shared,
          groups: [],
          viewerCredentials: false,
          sharingScope: CommonSharingScope.Specific
        })
      }
      setMode(mode)
    },
    [update, connection.viewerCredentials]
  )

  const handleOnChooseGroups = useCallback(
    (groups) => {
      update({
        privacy: CommonPrivacy.Shared,
        groups,
        viewerCredentials: false,
        sharingScope: CommonSharingScope.Specific
      })
      setGroups(groups)
    },
    [update]
  )

  const description = useMemo(() => {
    if (privacy === CommonPrivacy.Shared) {
      const extraText = mode === 'viewer' && <span>(requires credentials)</span>
      return <SharedOptionDescription groups={groups} extraText={extraText} />
    }
    return
  }, [privacy, mode, groups])

  if (!open) {
    return null
  }

  const updatePrivacyAfterWarning = () => {
    updatePrivacy(CommonPrivacy.Private)
    setShowUnshareConnectionWarning(false)
  }

  const disableViewerCredentialsAfterWarning = () => {
    update({
      privacy: CommonPrivacy.Shared,
      groups: [],
      viewerCredentials: false,
      sharingScope: CommonSharingScope.Organization
    })
    setShowDisableViewerCredentialWarning(false)
  }

  const cancelAfterWarning = () => {
    setPrivacy(connection.privacy as CommonPrivacy)

    if (connection.groups?.length) {
      setMode('groups')
    } else if (
      connection.viewerCredentials ||
      isBQWithSSO ||
      isBQWithWIF ||
      isSnowflakeWithSSO ||
      isDatabricksWithU2m
    ) {
      setMode('viewer')
    } else {
      setMode('organization')
    }

    setShowDisableViewerCredentialWarning(false)
    setShowUnshareConnectionWarning(false)
  }

  const handleClose = () => {
    if (!dirty) {
      onClose()
      return
    }
    setShowNotSavedChangesAlert(true)
  }

  const SharedWithOrganizationComponent = providerSupportViewerCredentials
    ? ConnectionSharedWithOrganizationMode
    : SharedWithOrganizationMode
  return (
    <Dialog open={open} size="medium" height="auto">
      <DialogHeader title={<FormattedMessage id="common.shareDialog.title" />} onClose={handleClose} />
      <DialogContent>
        <PrivacySettingsRadioGroup
          value={privacy}
          onChange={handleChangeShareOption}
          options={[PrivatePrivacy, SharedPrivacyOption]}
          description={description}
        />
        {privacy === CommonPrivacy.Shared && (
          <SharedWithOrganizationComponent
            mode={mode}
            groups={groups}
            sharingScope={connection.sharingScope}
            onChangeMode={handleOnSharingOrganizationMode}
            onChangeGroups={handleOnChooseGroups}
            disabledEntireOrganization={disabledEntireOrganization || disableEntireOrganization}
            tooltipEntireOrganization={tooltipEntireOrganization}
            disabledGroups={disabledGroups}
            tooltipGroups={tooltipGroups}
            disabledViewerCredentials={isViewerCredentialsDisabled}
            tooltipViewerCredentials={tooltipViewerCredentials}
          />
        )}
      </DialogContent>
      <ConnectionShareDialogFooter onClose={handleClose} loading={isUpdating} />
      {showDisableViewerCredentialWarning && (
        <DisableViewerCredentialsConfirmation
          onConfirm={disableViewerCredentialsAfterWarning}
          onCancel={cancelAfterWarning}
        />
      )}
      {showUnshareConnectionWarning && (
        <UnsharingConnectionWarning onConfirm={updatePrivacyAfterWarning} onCancel={cancelAfterWarning} />
      )}
      {showNotSavedChangesAlert && (
        <DialogConfirmation
          opened
          title={<FormattedMessage id="connections.shareDialog.notShared" />}
          actions={
            <>
              <Button onClick={() => setShowNotSavedChangesAlert(false)}>
                <FormattedMessage id="common.button.cancel" />
              </Button>
              <Button onClick={onClose}>
                <FormattedMessage id="common.button.discard" />
              </Button>
            </>
          }
        >
          <FormattedMessage id="connections.shareDialog.notSharedDescription" />
        </DialogConfirmation>
      )}
    </Dialog>
  )
}

export default ConnectionShareDialogUiWrapper
